import { useEffect, useRef } from 'react';
import { Link, NavLink, useLocation } from 'react-router';
import { useTranslations } from 'use-intl';
import { X } from 'lucide-react';
import { LOCALES, type Locale } from '@/i18n';
import { localePath, swapLocale } from '@/app/locale';
import { useSession } from '@/app/session';
import { Button } from '@/ui/Button';
import { GROUPS, PINNED } from '@/lib/nav';
import { NAV_ICONS } from './icons';

/**
 * Everything the bottom bar does not have room for.
 *
 * A list, not a row that scrolls sideways. The whole point of the rebuild is
 * that finding a screen is reading rather than guessing, so every destination
 * in the product is visible here under a heading that says which part of the
 * week it belongs to.
 *
 * It closes on Escape, on the backdrop, and on navigating — the old menu did
 * none of those and had no focus management either, so a keyboard or
 * screen-reader user was left inside a band that stayed open behind the page.
 */
export function MoreSheet({
  locale,
  open,
  onClose,
  onSignOut,
}: {
  locale: Locale;
  open: boolean;
  onClose: () => void;
  onSignOut: () => Promise<void> | void;
}) {
  const t = useTranslations();
  const { user } = useSession();
  const { pathname } = useLocation();
  const closeRef = useRef<HTMLButtonElement>(null);

  // Closing on navigation is what makes tapping a destination feel like going
  // there rather than like opening a second thing.
  useEffect(() => {
    if (open) onClose();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  useEffect(() => {
    if (!open) return;

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);

    // The sheet covers the page, so the page must not scroll underneath it.
    const previous = document.documentElement.style.overflow;
    document.documentElement.style.overflow = 'hidden';

    closeRef.current?.focus();

    return () => {
      document.removeEventListener('keydown', onKey);
      document.documentElement.style.overflow = previous;
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-40 lg:hidden">
      <div
        aria-hidden="true"
        onClick={onClose}
        className="absolute inset-0 bg-ink/40 backdrop-blur-sm"
      />

      <div
        role="dialog"
        aria-modal="true"
        aria-label={t('nav.more')}
        className="absolute inset-0 flex flex-col bg-paper"
        style={{
          paddingTop: 'env(safe-area-inset-top, 0px)',
          paddingBottom: 'env(safe-area-inset-bottom, 0px)',
        }}
      >
        <header className="flex items-center gap-3 border-b border-line bg-surface px-4 py-2.5">
          <h2 className="font-condensed text-subhead font-bold tracking-tight">
            {t('nav.more')}
          </h2>
          <button
            ref={closeRef}
            type="button"
            onClick={onClose}
            aria-label={t('common.close')}
            className="ms-auto grid size-11 place-items-center rounded-(--radius-control) text-ink-muted transition-colors hover:bg-surface-sunken hover:text-ink"
          >
            <X aria-hidden="true" className="size-5" />
          </button>
        </header>

        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
          {/* The four most-opened screens that did not win a tab, so nothing in
              the product is more than two taps away. */}
          <nav aria-label={t('nav.quick')} className="border-b border-line">
            {/* Headed like the groups below it. Three of these four also appear
                in their own group, and without a label that reads as the same
                screen listed twice rather than as a shortcut to it. */}
            <h3 className="px-4 pt-4 pb-1 font-condensed text-meta font-semibold text-ink-faint">
              {t('nav.quick')}
            </h3>
            <ul className="grid grid-cols-2 gap-2 border-t border-line bg-surface p-4">
              {PINNED.map((item) => {
                const Icon = NAV_ICONS[item.key];
                return (
                <li key={item.key}>
                  <NavLink
                    to={localePath(locale, item.to)}
                    end={item.end}
                    onClick={onClose}
                    className={({ isActive }) =>
                      `flex min-h-14 items-center gap-3 rounded-(--radius-control) border px-3 py-2 transition-colors ${
                        isActive
                          ? 'border-line-strong bg-signal-soft font-semibold'
                          : 'border-line bg-surface hover:border-line-strong'
                      }`
                    }
                  >
                    <Icon aria-hidden="true" className="size-5 shrink-0 text-ink-muted" />
                    <span className="min-w-0">{t(`nav.${item.key}`)}</span>
                  </NavLink>
                </li>
                );
              })}
            </ul>
          </nav>

          {GROUPS.map((group) => (
            <nav
              key={group.key}
              aria-label={t(`nav.group.${group.key}`)}
              className="border-b border-line"
            >
              <h3 className="px-4 pt-4 pb-1 font-condensed text-meta font-semibold text-ink-faint">
                {t(`nav.group.${group.key}`)}
              </h3>
              <ul className="divide-y divide-line border-t border-line bg-surface">
                {group.items.map((item) => {
                  const Icon = NAV_ICONS[item.key];
                  return (
                  <li key={item.key}>
                    <NavLink
                      to={localePath(locale, item.to)}
                      end={item.end}
                      onClick={onClose}
                      className={({ isActive }) =>
                        `flex min-h-12 items-center gap-3 px-4 py-2.5 transition-colors ${
                          isActive
                            ? 'bg-signal-soft font-semibold'
                            : 'hover:bg-surface-sunken'
                        }`
                      }
                    >
                      <Icon aria-hidden="true" className="size-5 shrink-0 text-ink-muted" />
                      <span className="min-w-0">{t(`nav.${item.key}`)}</span>
                    </NavLink>
                  </li>
                  );
                })}
              </ul>
            </nav>
          ))}

          {user?.isPlatformAdmin ? (
            <nav className="border-b border-line bg-surface">
              <Link
                to={localePath(locale, '/admin')}
                onClick={onClose}
                className="flex min-h-12 items-center gap-3 border-s-2 border-signal px-4 py-2.5 font-medium hover:bg-surface-sunken"
              >
                {t('nav.admin')}
              </Link>
            </nav>
          ) : null}

          {/* Full names rather than language codes: this is the one place a
              coach who opened the app in the wrong language has to find it. */}
          <nav aria-label={t('nav.language')} className="border-b border-line">
            <h3 className="px-4 pt-4 pb-1 font-condensed text-meta font-semibold text-ink-faint">
              {t('nav.language')}
            </h3>
            <ul className="divide-y divide-line border-t border-line bg-surface">
              {LOCALES.map((l) => (
                <li key={l}>
                  <Link
                    to={swapLocale(pathname, l)}
                    aria-current={l === locale ? 'page' : undefined}
                    className={`flex min-h-12 items-center px-4 py-2.5 ${
                      l === locale ? 'bg-signal-soft font-semibold' : 'hover:bg-surface-sunken'
                    }`}
                  >
                    {t(`locale.${l}`)}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>

          <div className="grid gap-3 p-4">
            {user ? <p className="text-meta text-ink-muted">{user.email}</p> : null}
            <Button variant="secondary" onClick={() => void onSignOut()}>
              {t('nav.signOut')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
